/**
 * The calls to action on the WyseHub marketing pages.
 *
 * The product screens are MUI, so there is no shared `Button` to lean on here; this is
 * the whole of the button styling for the marketing subtree. The classes are joined by
 * hand rather than through `clsx`/`tailwind-merge`, which the marketing pages do not
 * carry; nothing here composes conflicting utilities, so a join is enough.
 */

/** `wyseai` is the sibling brand's navy, for the one button that leaves for WyseAi. */
export type CtaVariant = 'primary' | 'secondary' | 'wyseai'
export type CtaSize = 'md' | 'lg'

const VARIANTS: Readonly<Record<CtaVariant, string>> = {
  primary: 'bg-brand text-brand-foreground hover:bg-brand-strong',
  secondary: 'border border-line bg-surface text-ink hover:bg-surface-muted',
  wyseai: 'bg-wyseai text-brand-foreground hover:bg-wyseai-strong',
}

// 44px / 48px targets: WCAG 2.2 AA 2.5.8 asks for at least 24×24 CSS px, and a
// comfortable touch target is the cheapest thing to get right at build time.
const SIZES: Readonly<Record<CtaSize, string>> = {
  md: 'h-11 px-4 text-base',
  lg: 'h-12 px-6 text-lg',
}

export interface CtaClassNameOptions {
  readonly variant?: CtaVariant
  readonly size?: CtaSize
  readonly className?: string
}

/**
 * A call to action: a pill, with a small scale on hover.
 *
 * The hover is a scale on a long ease rather than a colour change alone, so the button
 * reads as something that responds. Motion only for people who have not asked for less
 * of it. It is `scale`, not `transform`: Tailwind v4 scales with the individual `scale`
 * property, and the transition list names it because `transition-colors` alone would
 * leave the scale snapping.
 */
export function ctaClassName({
  variant = 'primary',
  size = 'md',
  className,
}: CtaClassNameOptions = {}): string {
  return [
    'inline-flex items-center justify-center gap-2 rounded-full font-medium',
    'transition-[background-color,color,scale] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]',
    'motion-safe:hover:scale-[1.04] motion-safe:active:scale-[0.97]',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
    'disabled:pointer-events-none disabled:opacity-60',
    VARIANTS[variant],
    SIZES[size],
    className,
  ]
    .filter(Boolean)
    .join(' ')
}

/**
 * A link inside running text on the public pages: underlined, so it reads as a link
 * without leaning on colour, with the same focus ring as everything else.
 *
 * One string for every inline link on the marketing pages, so they cannot drift apart.
 */
export const INLINE_LINK =
  'font-medium text-ink underline decoration-line underline-offset-4 transition-colors hover:decoration-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus'

/**
 * The same link on the dark band, in the `on-ink` tokens. `INLINE_LINK` is `text-ink`,
 * which is invisible on ink.
 */
export const INLINE_LINK_ON_INK =
  'font-medium text-on-ink underline decoration-line-on-ink underline-offset-4 transition-colors hover:decoration-on-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus'
